import { useEffect, useRef, useState, type RefObject } from 'react';

import { EyeBall, Pupil } from './Eyes';
import styles from './index.module.scss';
import { type CharacterAnimationState } from './use-character-animation';

type MousePosition = {
  readonly x: number;
  readonly y: number;
};

type CharacterProps = CharacterAnimationState & {
  readonly mousePosition: MousePosition;
};

const useMousePosition = (): MousePosition => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      setMousePosition({ x: event.clientX, y: event.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);

  return mousePosition;
};

const calculatePosition = (
  ref: RefObject<HTMLDivElement>,
  { x: mouseX, y: mouseY }: MousePosition
) => {
  if (!ref.current) {
    return { faceX: 0, faceY: 0, bodySkew: 0 };
  }

  const rect = ref.current.getBoundingClientRect();
  const centerX = rect.left + rect.width / 2;
  const centerY = rect.top + rect.height / 3;
  const deltaX = mouseX - centerX;
  const deltaY = mouseY - centerY;

  return {
    faceX: Math.max(-15, Math.min(15, deltaX / 20)),
    faceY: Math.max(-10, Math.min(10, deltaY / 30)),
    bodySkew: Math.max(-6, Math.min(6, -deltaX / 120)),
  };
};

const PurpleCharacter = ({
  mousePosition,
  isTyping,
  hasPassword,
  showPassword,
  isPurpleBlinking,
  isLookingAtEachOther,
  isPurplePeeking,
}: CharacterProps) => {
  const characterRef = useRef<HTMLDivElement>(null);
  const position = calculatePosition(characterRef, mousePosition);
  const isLeaning = isTyping || (hasPassword && !showPassword);
  const isCovering = hasPassword && showPassword;

  return (
    <div
      ref={characterRef}
      className={styles.character}
      style={{
        left: '70px',
        width: '180px',
        height: isLeaning ? '440px' : '400px',
        backgroundColor: '#6C3FF5',
        borderRadius: '10px 10px 0 0',
        zIndex: 1,
        transform: isCovering
          ? 'skewX(0deg)'
          : isLeaning
            ? `skewX(${position.bodySkew - 12}deg) translateX(40px)`
            : `skewX(${position.bodySkew}deg)`,
        transformOrigin: 'bottom center',
      }}
    >
      <div
        className={styles.eyes}
        style={{
          gap: '32px',
          left: isCovering ? '20px' : isLookingAtEachOther ? '55px' : `${45 + position.faceX}px`,
          top: isCovering ? '35px' : isLookingAtEachOther ? '65px' : `${40 + position.faceY}px`,
        }}
      >
        {[0, 1].map((index) => (
          <EyeBall
            key={index}
            size={18}
            pupilSize={7}
            maxDistance={5}
            eyeColor="white"
            pupilColor="#2D2D2D"
            isBlinking={isPurpleBlinking}
            forceLookX={
              isCovering ? (isPurplePeeking ? 4 : -4) : isLookingAtEachOther ? 3 : undefined
            }
            forceLookY={
              isCovering ? (isPurplePeeking ? 5 : -4) : isLookingAtEachOther ? 4 : undefined
            }
          />
        ))}
      </div>
    </div>
  );
};

const BlackCharacter = ({
  mousePosition,
  isTyping,
  hasPassword,
  showPassword,
  isBlackBlinking,
  isLookingAtEachOther,
}: CharacterProps) => {
  const characterRef = useRef<HTMLDivElement>(null);
  const position = calculatePosition(characterRef, mousePosition);
  const isLeaning = isTyping || (hasPassword && !showPassword);
  const isCovering = hasPassword && showPassword;

  return (
    <div
      ref={characterRef}
      className={styles.character}
      style={{
        left: '240px',
        width: '120px',
        height: '310px',
        backgroundColor: '#2D2D2D',
        borderRadius: '8px 8px 0 0',
        zIndex: 2,
        transform: isCovering
          ? 'skewX(0deg)'
          : isLookingAtEachOther
            ? `skewX(${position.bodySkew * 1.5 + 10}deg) translateX(20px)`
            : isLeaning
              ? `skewX(${position.bodySkew * 1.5}deg)`
              : `skewX(${position.bodySkew}deg)`,
        transformOrigin: 'bottom center',
      }}
    >
      <div
        className={styles.eyes}
        style={{
          gap: '24px',
          left: isCovering ? '10px' : isLookingAtEachOther ? '32px' : `${26 + position.faceX}px`,
          top: isCovering ? '28px' : isLookingAtEachOther ? '12px' : `${32 + position.faceY}px`,
        }}
      >
        {[0, 1].map((index) => (
          <EyeBall
            key={index}
            size={16}
            pupilSize={6}
            maxDistance={4}
            eyeColor="white"
            pupilColor="#2D2D2D"
            isBlinking={isBlackBlinking}
            forceLookX={isCovering ? -4 : isLookingAtEachOther ? 0 : undefined}
            forceLookY={isCovering ? -4 : isLookingAtEachOther ? -4 : undefined}
          />
        ))}
      </div>
    </div>
  );
};

const OrangeCharacter = ({ mousePosition, hasPassword, showPassword }: CharacterProps) => {
  const characterRef = useRef<HTMLDivElement>(null);
  const position = calculatePosition(characterRef, mousePosition);
  const isCovering = hasPassword && showPassword;

  return (
    <div
      ref={characterRef}
      className={styles.character}
      style={{
        left: '0px',
        width: '240px',
        height: '200px',
        zIndex: 3,
        backgroundColor: '#FF9B6B',
        borderRadius: '120px 120px 0 0',
        transform: isCovering ? 'skewX(0deg)' : `skewX(${position.bodySkew}deg)`,
        transformOrigin: 'bottom center',
      }}
    >
      <div
        className={styles.pupils}
        style={{
          gap: '32px',
          left: isCovering ? '50px' : `${82 + position.faceX}px`,
          top: isCovering ? '85px' : `${90 + position.faceY}px`,
        }}
      >
        {[0, 1].map((index) => (
          <Pupil
            key={index}
            size={12}
            maxDistance={5}
            pupilColor="#2D2D2D"
            forceLookX={isCovering ? -5 : undefined}
            forceLookY={isCovering ? -4 : undefined}
          />
        ))}
      </div>
    </div>
  );
};

const YellowCharacter = ({ mousePosition, hasPassword, showPassword }: CharacterProps) => {
  const characterRef = useRef<HTMLDivElement>(null);
  const position = calculatePosition(characterRef, mousePosition);
  const isCovering = hasPassword && showPassword;

  return (
    <div
      ref={characterRef}
      className={styles.character}
      style={{
        left: '310px',
        width: '140px',
        height: '230px',
        backgroundColor: '#E8D754',
        borderRadius: '70px 70px 0 0',
        zIndex: 4,
        transform: isCovering ? 'skewX(0deg)' : `skewX(${position.bodySkew}deg)`,
        transformOrigin: 'bottom center',
      }}
    >
      <div
        className={styles.pupils}
        style={{
          gap: '24px',
          left: isCovering ? '20px' : `${52 + position.faceX}px`,
          top: isCovering ? '35px' : `${40 + position.faceY}px`,
        }}
      >
        {[0, 1].map((index) => (
          <Pupil
            key={index}
            size={12}
            maxDistance={5}
            pupilColor="#2D2D2D"
            forceLookX={isCovering ? -5 : undefined}
            forceLookY={isCovering ? -4 : undefined}
          />
        ))}
      </div>
      <div
        className={styles.mouth}
        style={{
          left: isCovering ? '10px' : `${40 + position.faceX}px`,
          top: isCovering ? '88px' : `${88 + position.faceY}px`,
        }}
      />
    </div>
  );
};

const Characters = (animationState: CharacterAnimationState) => {
  const mousePosition = useMousePosition();
  const props = { ...animationState, mousePosition };

  return (
    <div className={styles.stageWrapper}>
      <div className={styles.stage}>
        <PurpleCharacter {...props} />
        <BlackCharacter {...props} />
        <OrangeCharacter {...props} />
        <YellowCharacter {...props} />
      </div>
    </div>
  );
};

export default Characters;
